import { createAsyncThunk } from "@reduxjs/toolkit";
import { TOAST_VARIANT } from "@root/constants";
import { ChatService } from "@services/index";
import { t } from "i18next";
import {
  BULK_DELETE_THREADS,
  CHAT_FEEDBACK,
  DELETE_THREADS,
  GET_PROFILE,
  GET_RECENT_HISTORY,
  GET_THREADS,
  GET_THREAD_BY_ID,
  GET_THREAD_BY_KUID,
  GET_THREAD_BY_UID,
  SEND_INPUT,
  STOP_CHAT,
} from "../action-types";
import { getChatPage, getChatPageCount } from "../selectors/entities";
import { getThreadPage } from "../selectors/entities/thread";
import { getBaseUrl, getPage } from "../selectors/features";
import { showAlert } from "../slices/features";
import { WEB_TABLES_MAX_LIMIT } from "@root/webConstants";
import { PDF_PREVIEW } from "@root/services/chat/endpoints";

const chatService = new ChatService();

export const sendInput = createAsyncThunk<TObject, TObject, IActionOptions>(
  SEND_INPUT,
  async (requestPayload: any, thunkAPI) => {
    const baseUrl = getBaseUrl(thunkAPI.getState());
    const { payload, onError, onSuccess, withPdf, pid } = requestPayload;
    const res = await chatService.sendInput(baseUrl, payload, withPdf, pid);
    const { data, status } = res?.response || res;

    if (status !== 200) {
      onError(data);
      return thunkAPI.rejectWithValue(data);
    } else {
      onSuccess({ ...data?.data, withPdf });
    }

    return thunkAPI.fulfillWithValue(data);
  }
);

export const sendReport = createAsyncThunk<TObject, TObject, IActionOptions>(
  CHAT_FEEDBACK,
  async (requestPayload: any, thunkAPI) => {
    const baseUrl = getBaseUrl(thunkAPI.getState());
    const { payload, onSuccess } = requestPayload;
    const { data: response } = await chatService.sendReport(baseUrl, payload);
    const { data } = response;

    if (response?.error || typeof response === "string") {
      thunkAPI.dispatch(
        showAlert({
          message: t("common:feedback.somethingWrong"),
          type: TOAST_VARIANT.ERROR,
        })
      );
      return thunkAPI.rejectWithValue(data?.error_report);
    } else {
      onSuccess();
      thunkAPI.dispatch(
        showAlert({
          message: t("common:feedback.thanksForFeedback"),
          type: TOAST_VARIANT.SUCCESS,
        })
      );
    }

    return thunkAPI.fulfillWithValue(data);
  }
);

export const getThreads = createAsyncThunk<TObject, TObject, IActionOptions>(
  GET_THREADS,
  async (requestPayload: any, thunkAPI) => {
    const baseUrl = getBaseUrl(thunkAPI.getState());
    const page = getPage(thunkAPI.getState());
    const { onError, onSuccess, params } = requestPayload;

    const { data, error } = await chatService.getThreads(baseUrl, {
      page,
      limit: WEB_TABLES_MAX_LIMIT,
      ...params,
    });

    if (error) {
      onError(error);
      return thunkAPI.rejectWithValue(error);
    } else {
      onSuccess(data);
    }
    return thunkAPI.fulfillWithValue(data);
  }
);

export const getThreadByID = createAsyncThunk<TObject, TObject, IActionOptions>(
  GET_THREAD_BY_ID,
  async (requestPayload: any, thunkAPI) => {
    const baseUrl = getBaseUrl(thunkAPI.getState());
    const page = getThreadPage(thunkAPI.getState());
    const pageSize = getChatPageCount(thunkAPI.getState());
    const { onError, onSuccess, payload, pid } = requestPayload;

    const pageInfo = {
      page,
      limit: pageSize,
    };

    const { data, error } = await chatService.getThreadByID(
      baseUrl,
      payload?.chatId,
      pageInfo,
      pid
    );
    if (error) {
      onError(error);
      return thunkAPI.rejectWithValue(error);
    } else {
      onSuccess(data);
    }
    return thunkAPI.fulfillWithValue(data);
  }
);

export const getThreadByUID = createAsyncThunk<
  TObject,
  TObject,
  IActionOptions
>(GET_THREAD_BY_UID, async (requestPayload: any, thunkAPI) => {
  const baseUrl = getBaseUrl(thunkAPI.getState());
  const page = getChatPage(thunkAPI.getState());
  const pageSize = getChatPageCount(thunkAPI.getState());
  const { onError, onSuccess, payload } = requestPayload;

  const pageInfo = {
    page,
    limit: pageSize,
  };

  const { data, error } = await chatService.getThreadByUID(
    baseUrl,
    payload?.uid,
    pageInfo
  );
  if (error) {
    onError(error);
    return thunkAPI.rejectWithValue(error);
  } else {
    onSuccess(data);
  }
  return thunkAPI.fulfillWithValue(data);
});
export const getThreadByKUID = createAsyncThunk<
  TObject,
  TObject,
  IActionOptions
>(GET_THREAD_BY_KUID, async (requestPayload: any, thunkAPI) => {
  const baseUrl = getBaseUrl(thunkAPI.getState());
  const page = getChatPage(thunkAPI.getState());
  const pageSize = getChatPageCount(thunkAPI.getState());
  const { onError, onSuccess, payload } = requestPayload;

  const pageInfo = {
    page,
    limit: pageSize,
    thread: 1,
  };

  const { data, error } = await chatService.getThreadByKUID(
    baseUrl,
    payload?.thraedUID,
    pageInfo
  );
  if (error) {
    onError(error);
    return thunkAPI.rejectWithValue(error);
  } else {
    onSuccess(data);
  }
  return thunkAPI.fulfillWithValue(data);
});

export const DeleteThreads = createAsyncThunk<TObject, TObject, IActionOptions>(
  DELETE_THREADS,
  async (requestPayload: any, thunkAPI) => {
    const baseUrl = getBaseUrl(thunkAPI.getState());
    const { payload, onSuccess, onError } = requestPayload;
    const { error } = await chatService.deleteThreads(baseUrl, payload);
    if (error) {
      onError();
      return thunkAPI.rejectWithValue(error);
    }
    onSuccess();
    return thunkAPI.fulfillWithValue(payload);
  }
);

export const getRecentThreadHistory = createAsyncThunk<
  TObject,
  TObject,
  IActionOptions
>(GET_RECENT_HISTORY, async (requestPayload: any, thunkAPI) => {
  const baseUrl = getBaseUrl(thunkAPI.getState());
  const { payload, onSuccess, onError } = requestPayload;
  const { error, data } = await chatService.getRecentHistory(
    baseUrl,
    payload?.threadId
  );
  if (error) {
    onError();
    return thunkAPI.rejectWithValue(error);
  }
  onSuccess(data);
  return thunkAPI.fulfillWithValue(payload);
});

export const getUserProfile = createAsyncThunk<
  TObject,
  TObject,
  IActionOptions
>(GET_PROFILE, async (requestPayload: any, thunkAPI) => {
  const baseUrl = getBaseUrl(thunkAPI.getState());
  const { onSuccess, onError } = requestPayload;
  const res = await chatService.getUserProfile(baseUrl);
  if (!res?.data?.data) {
    onError();
    return thunkAPI.rejectWithValue({});
  }
  if (onSuccess) {
    onSuccess();
  }
  return thunkAPI.fulfillWithValue(res?.data?.data);
});

export const bulkDeleteThreads = createAsyncThunk<
  TObject,
  TObject,
  IActionOptions
>(BULK_DELETE_THREADS, async (requestPayload: any, thunkAPI) => {
  const baseUrl = getBaseUrl(thunkAPI.getState());
  const { payload, onSuccess, onError } = requestPayload;
  const { error } = await chatService.bulkDeleteThreads(baseUrl);

  if (error) {
    onError();
    return thunkAPI.rejectWithValue(error);
  }
  if (onSuccess) {
    onSuccess();
  }
  return thunkAPI.fulfillWithValue(payload);
});

export const getPdfPreviewThunk = createAsyncThunk<
  TObject,
  TObject,
  IActionOptions
>(PDF_PREVIEW, async (requestPayload: any, thunkAPI) => {
  const baseUrl = getBaseUrl(thunkAPI.getState());
  const { payload, onSuccess, onError } = requestPayload;
  const { error, data } = await chatService.getPdfPreview(baseUrl, payload);

  if (error) {
    onError?.();
    return thunkAPI.rejectWithValue(error);
  }
  onSuccess?.(data);
  return thunkAPI.fulfillWithValue(payload);
});

export const isPdfUrlValidThunk = createAsyncThunk<
  TObject,
  TObject,
  IActionOptions
>(PDF_PREVIEW, async (requestPayload: any, thunkAPI) => {
  const baseUrl = getBaseUrl(thunkAPI.getState());
  const { payload, onSuccess, onError } = requestPayload;
  const { error, data } = await chatService.isPdfUrlValid(baseUrl, payload);

  if (error) {
    onError?.();
    return thunkAPI.rejectWithValue(error);
  }
  onSuccess?.(data);
  return thunkAPI.fulfillWithValue(data);
});

export const downloadPdfThread = createAsyncThunk<
  TObject,
  TObject,
  IActionOptions
>(PDF_PREVIEW, async (requestPayload: any, thunkAPI) => {
  const baseUrl = getBaseUrl(thunkAPI.getState());
  const { payload, onSuccess, onError } = requestPayload;
  const { error, data } = await chatService.downloadThreadPdf(baseUrl, payload);

  if (error) {
    onError?.();
    return thunkAPI.rejectWithValue(error);
  }
  onSuccess?.(data);
  return thunkAPI.fulfillWithValue(payload);
});

export const stopChat = createAsyncThunk<TObject, TObject, IActionOptions>(
  STOP_CHAT,
  async (requestPayload: any, thunkAPI) => {
    const baseUrl = getBaseUrl(thunkAPI.getState());
    const { chatId, onSuccess, onError } = requestPayload;
    const { error, data } = await chatService.stopChat(baseUrl, chatId);

    if (error) {
      onError?.(error);
      return thunkAPI.rejectWithValue(error);
    }
    onSuccess?.(data);
    return thunkAPI.fulfillWithValue(data);
  }
);
